<template>
    <form @submit.prevent="addToBookmarks" class="bookmark-form">
        <!-- Variant attribute selection with radio buttons -->
        <div v-if="hasVariants">

            <div class="selection_container">
                <h3>{{ translations.cameraConfiguration }}</h3>
                <div
                    v-for="group in variantAttributeGroups"
                    :key="group.key"
                    class="selection_container__group"
                >
                    <h4>{{ group.label }}</h4>
                    <div
                        v-for="option in group.options"
                        :key="option.value"
                        class="radio_wrapper input-button"
                        :class="{ 'input-button--not-available': !option.available }"
                    >
                        <input
                            type="radio"
                            :id="`variant-${group.key}-${option.value}`"
                            :name="`variant-${group.key}`"
                            :value="option.value"
                            :checked="variantSelections[group.key] === option.value"
                            @change="setVariantAttribute(group.key, option.value)"
                        >
                        <label :for="`variant-${group.key}-${option.value}`">
                            {{ option.value === '__none__' ? (translations.variantNotSelected || 'Not selected') : option.value }}
                        </label>
                    </div>
                </div>
            </div>

        </div>

        <!-- Bundle-Auswahl (wenn vorhanden) -->
        <div v-if="hasBundles" class="selection_container">
            <h3>{{ translations.selectBundle }}</h3>

            <!-- No bundle option -->
            <div class="radio_wrapper input-button">
                <input
                    type="radio"
                    id="bundle-none"
                    name="bundle"
                    value=""
                    v-model="selectedBundle"
                >
                <label for="bundle-none">{{ translations.noBundle }}</label>
            </div>

            <!-- Bundle options -->
            <template v-for="(bundle, bundleIndex) in bundles" :key="bundle.id">
                <div
                    v-for="(product, productIndex) in bundle.products"
                    :key="`${bundle.id}-${product.id}`"
                    class="radio_wrapper input-button"
                >
                    <input
                        type="radio"
                        :id="`bundle-${bundle.id}-${product.id}`"
                        name="bundle"
                        :value="`${bundle.id}:${product.productId}`"
                        v-model="selectedBundle"
                    >
                    <label :for="`bundle-${bundle.id}-${product.id}`">
                        {{ product.title }}
                        <span v-if="product.description" class="bundle-description">
                            {{ product.description }}
                        </span>
                        <a
                            v-if="product.link"
                            :href="product.link"
                            class="bundle-info-link"
                            @click.stop
                            target="_blank"
                        >
                            {{ translations.moreInfo || 'Info' }}
                        </a>
                    </label>
                </div>
            </template>
        </div>

        <!-- Accessories Selection (when available) -->
        <div v-if="hasAnyAccessories" class="selection_container">
            <h3>{{ accessoriesTranslations.heading }}</h3>

            <template v-for="category in accessoryCategories" :key="category.key">
                <div v-if="category.items.length > 0" class="selection_container__accessory">
                    <details class="animated-details">
                        <summary>
                            {{ category.label }}
                            <span v-if="selectedAccessories[category.key].length > 0" class="selection_container__accessory-count">
                                ({{ selectedAccessories[category.key].length }})
                            </span>
                        </summary>
                        <div class="details__content">
                            <div class="details__content-inner">
                                <ul>
                                    <li
                                        v-for="item in category.items"
                                        :key="item.id"
                                        class="selection_container__accessory-item"
                                    >
                                        <label class="selection_container__accessory-item-label">
                                            <input
                                                type="checkbox"
                                                :checked="selectedAccessories[category.key].includes(item.id)"
                                                @change="toggleAccessory(category.key, item.id)"
                                                class="selection_container__accessory-item-checkbox"
                                                :aria-label="readableName(item.title)"
                                            >
                                            <span aria-hidden="true" class="selection_container__accessory-item-title">{{ accessoryShownName(item) }}</span>
                                        </label>
                                        <button
                                            type="button"
                                            @click.stop="toggleItemDetails(category.key, item.id, $event)"
                                            class="selection_container__accessory-item-info-toggle"
                                            :aria-label="moreInformationLabel(item)"
                                            :aria-expanded="isItemDetailsOpen(category.key, item.id) ? 'true' : 'false'"
                                        ><span aria-hidden="true">i</span></button>
                                        <teleport to="body" v-if="isItemDetailsOpen(category.key, item.id)">
                                            <div
                                                class="selection_container__accessory-item-details"
                                                :style="getItemDetailsStyle(category.key, item.id)"
                                            >
                                                <h5>{{ item.title }}</h5>
                                                <dl class="product-specs">
                                                    <template v-for="spec in item.specs" :key="spec.label">
                                                        <dt>{{ spec.label }}</dt>
                                                        <dd>{{ spec.value }}</dd>
                                                    </template>
                                                </dl>
                                                <a
                                                    v-if="item.link"
                                                    :href="item.link"
                                                    class="selection_container__accessory-item-link"
                                                    target="_blank"
                                                >{{ translations.moreInfo || 'Info' }}</a>
                                            </div>
                                        </teleport>

                                    </li>
                                </ul>
                            </div>
                        </div>
                    </details>
                </div>
            </template>
        </div>

        <!-- Summary: configured product + selected addons -->
        <div v-if="showSummary" class="selection_container variant-match">
            <h3>{{ translations.configuredProduct }}</h3>
            <div class="selected-product">
                <div class="selected-product-info">
                    <!-- Product heading -->
                    <p class="selected-product-label" v-if="matchedVariant || productTitle || selectedBundleProduct">{{ translations.summaryProductLabel || 'Product' }}</p>

                    <!-- Variant product or single product -->
                    <div v-if="matchedVariant" class="selected-product-name">
                        {{ productTitle }} {{ matchedVariant.title }}
                    </div>
                    <div v-else-if="productTitle" class="selected-product-name">
                        {{ productTitle }}
                        <span v-if="productCode" class="selected-product-code">{{ productCode }}</span>
                    </div>

                    <!-- Bundle product -->
                    <div v-if="selectedBundleProduct" class="selected-product-addon">
                        {{ selectedBundleProduct.title }}
                    </div>

                    <!-- Selected accessories -->
                    <template v-if="selectedAccessoryItems.length > 0">
                        <p class="selected-product-label selected-product-label--accessories">{{ translations.summaryAccessoriesLabel || 'Accessories' }}</p>
                        <ul class="selected-product-accessories">
                            <li v-for="item in selectedAccessoryItems" :key="`summary-${item.type}-${item.id}`" class="selected-product-accessory">
                                <span class="selected-product-accessory-category">{{ item.categoryLabel }}:</span>
                                {{ item.title }}
                            </li>
                        </ul>
                    </template>
                </div>
                <!-- Product image — rendered after info in DOM, moved left via CSS order -->
                <img
                    v-if="matchedVariantImageUrl"
                    :src="matchedVariantImageUrl"
                    :alt="matchedVariant ? matchedVariant.title : productTitle"
                    class="selected-product-image"
                >
            </div>
        </div>

        <!-- Submit Button -->
        <button
            type="submit"
            class="link--button link--button--primary"
            :disabled="isLoading || needsConfiguration"
        >
            <span v-if="isLoading">{{ translations.adding }}</span>
            <span v-else-if="needsConfiguration">{{ translations.chooseConfiguration || 'Please choose a configuration' }}</span>
            <span v-else>{{ translations.addToBookmarks }}</span>
        </button>
    </form>
</template>

<script>
import { initAccordions } from '../js/accordion-animation.js';

export default {
    name: 'BookmarkButton',

    props: {
        productID: { type: Number, required: true },
        productTitle: { type: String, default: '' },
        productCode: { type: String, default: '' },
        variants: { type: Array, default: () => [] },
        bundles: { type: Array, default: () => [] },
        accessories: { type: Object, default: () => ({ lenses: [], housing: [], mountingFixation: [], cooling: [], boards: [], interfaceConnections: [], lightingConnections: [], powerIoConnections: [], powerSupplyConnections: [] }) },
        apiUrl: { type: String, required: true },
        specsUrl: { type: String, default: '' },
        translations: { type: Object, default: () => ({}) },
        accessoriesTranslations: { type: Object, default: () => ({}) },
        imageFit: { type: String, default: '970x678' },
        imageOptimizeFormat: { type: Boolean, default: false },
    },

    data() {
        return {
            variantSelections: {
                chroma: '',
                housing: '',
                lensMount: '',
                angle: '',
                interfaceConnector: '',
            },
            urlVariantId: null,
            selectedBundle: '',
            selectedAccessories: {
                lenses: [],
                housing: [],
                mountingFixation: [],
                cooling: [],
                boards: [],
                interfaceConnections: [],
                lightingConnections: [],
                powerIoConnections: [],
                powerSupplyConnections: [],
            },
            openItemDetails: {},
            itemDetailsPosition: {},
            isLoading: false,
        };
    },

    computed: {
        hasVariants() {
            return this.variants && this.variants.length > 0;
        },
        hasBundles() {
            return this.bundles && this.bundles.length > 0;
        },
        hasAnyAccessories() {
            return this.accessoryCategories.some(c => c.items.length > 0);
        },

        accessoryCategories() {
            const map = [
                { key: 'lenses', label: this.accessoriesTranslations.lenses, type: 'Lens' },
                { key: 'housing', label: this.accessoriesTranslations.housing, type: 'Housing' },
                { key: 'mountingFixation', label: this.accessoriesTranslations.mountingFixation, type: 'MountingFixation' },
                { key: 'cooling', label: this.accessoriesTranslations.cooling, type: 'Cooling' },
                { key: 'boards', label: this.accessoriesTranslations.boards, type: 'Board' },
                { key: 'interfaceConnections', label: this.accessoriesTranslations.interfaceConnections, type: 'InterfaceConnection' },
                { key: 'lightingConnections', label: this.accessoriesTranslations.lightingConnections, type: 'LightingConnection' },
                { key: 'powerIoConnections', label: this.accessoriesTranslations.powerIoConnections, type: 'PowerIoConnection' },
                { key: 'powerSupplyConnections', label: this.accessoriesTranslations.powerSupplyConnections, type: 'PowerSupplyConnection' },
            ];
            return map.map(c => ({
                ...c,
                items: this.accessories[c.key] || [],
            }));
        },

        variantAttributeLabels() {
            return {
                chroma: this.translations.variantChroma || 'Chroma',
                housing: this.translations.variantHousing || 'Housing Type',
                lensMount: this.translations.variantLensMount || 'Lens Mount',
                angle: this.translations.variantAngle || 'Angle',
                interfaceConnector: this.translations.variantInterfaceConnector || 'Interface Connector',
            };
        },

        /**
         * Attribute keys that offer a real choice across variants: either
         * multiple distinct values, or a single value that some variants
         * don't have (choice between the value and "not selected").
         * Keys where all variants share the same value are not shown —
         * they can't distinguish variants.
         */
        activeVariantKeys() {
            if (!this.hasVariants) return [];

            const keys = ['chroma', 'housing', 'lensMount', 'angle', 'interfaceConnector'];
            return keys.filter(key => {
                const allValues = this.variants.map(v => v.attributes?.[key] || '');
                const uniqueValues = new Set(allValues.filter(v => v !== ''));
                const hasEmptyVariants = allValues.some(v => v === '');
                return uniqueValues.size >= 2 || (uniqueValues.size === 1 && hasEmptyVariants);
            });
        },

        /**
         * Build attribute groups with radio button options and availability state.
         * Adds a "not selected" virtual option when some variants lack a value for this key.
         */
        variantAttributeGroups() {
            return this.activeVariantKeys.map(key => {
                const allRawValues = this.variants.map(v => v.attributes?.[key] || '');
                const realValues = allRawValues.filter(v => v !== '');
                const uniqueValues = [...new Set(realValues)];
                const hasEmptyVariants = allRawValues.some(v => v === '');

                const options = uniqueValues.map(value => ({
                    value,
                    available: this.isOptionAvailable(key, value),
                }));

                // Add "not selected" option when there's only one real option
                // and some variants exist without this attribute, allowing deselection
                if (hasEmptyVariants && uniqueValues.length === 1) {
                    options.unshift({
                        value: '__none__',
                        available: this.isOptionAvailable(key, '__none__'),
                    });
                }

                return {
                    key,
                    label: this.variantAttributeLabels[key],
                    options,
                };
            });
        },

        /**
         * Find the variant matching all current attribute selections.
         * __none__ matches variants with empty attribute values.
         * Keys where the variant has no value and no selection was made are skipped.
         */
        matchedVariant() {
            if (!this.hasVariants) return null;

            // No attribute distinguishes the variants (e.g. they differ only in
            // product code): fall back to the variant from the URL or the first one
            if (this.activeVariantKeys.length === 0) {
                return this.variants.find(v => v.id === this.urlVariantId) || this.variants[0];
            }

            // Must have at least one selection
            const hasAnySelection = this.activeVariantKeys.some(k => this.variantSelections[k] !== '');
            if (!hasAnySelection) return null;

            return this.variants.find(v => {
                return this.activeVariantKeys.every(key => {
                    const attr = v.attributes?.[key] || '';
                    const sel = this.variantSelections[key];

                    // No selection and variant has no value — compatible
                    if (!sel && attr === '') return true;
                    // No selection but variant has a value — not a match
                    if (!sel && attr !== '') return false;
                    // Selection made but variant has no value — not a match
                    if (sel && sel !== '__none__' && attr === '') return false;

                    if (sel === '__none__') {
                        return attr === '';
                    }
                    return attr === sel;
                });
            }) || null;
        },

        /**
         * Backwards-compatible selectedVariant for URL/API usage.
         */
        selectedVariant() {
            return this.matchedVariant ? this.matchedVariant.id : '';
        },

        /**
         * The currently selected bundle product, resolved from the selectedBundle string.
         */
        selectedBundleProduct() {
            if (!this.selectedBundle) return null;
            const [bundleId, productId] = this.selectedBundle.split(':');
            for (const bundle of this.bundles) {
                if (String(bundle.id) === bundleId) {
                    return bundle.products.find(p => String(p.productId) === productId) || null;
                }
            }
            return null;
        },

        /**
         * Flat list of all selected accessory items with their category label.
         */
        selectedAccessoryItems() {
            const items = [];
            for (const category of this.accessoryCategories) {
                const ids = this.selectedAccessories[category.key] || [];
                for (const id of ids) {
                    const item = category.items.find(i => i.id === id);
                    if (item) {
                        items.push({
                            id: item.id,
                            title: item.title,
                            categoryLabel: category.label,
                            type: category.type,
                        });
                    }
                }
            }
            return items;
        },

        /**
         * Whether the summary section should be shown.
         */
        /**
         * True when the product has variants but the user has not yet selected a
         * complete configuration. Used to disable the submit button and show a
         * "please choose a configuration" hint instead of "add to bookmarks".
         */
        needsConfiguration() {
            return this.hasVariants && !this.matchedVariant;
        },

        showSummary() {
            // Only show the configured-product summary when there is something to
            // summarize. Pages without variants/bundles/accessories and no product
            // title (e.g. SingleProductPage, accessory detail pages) would otherwise
            // render an empty box above the submit button.
            return !!(
                this.matchedVariant
                || this.productTitle
                || this.selectedBundleProduct
                || (this.selectedAccessoryItems && this.selectedAccessoryItems.length > 0)
            );
        },

        /**
         * Parsed fit dimensions from the imageFit prop ("970x678" → {width:970, height:678}).
         */
        fitParams() {
            const [w, h] = this.imageFit.split('x').map(Number);
            return { width: w || null, height: h || null };
        },

        /**
         * URL to the product image proxy for the currently matched variant.
         * Returns null if no variant is matched or no image path is available.
         */
        matchedVariantImageUrl() {
            const path = this.matchedVariant?.productImageImport;
            if (!path) return null;
            return this.buildImageUrl(path);
        },
    },

    mounted() {
        // Store references for column image swapping
        const section = this.$el.closest('.section_content');
        this._sectionEl = section || null;
        this._sectionParentEl = section ? section.closest('section') : null;
        this._columnImageEl = section ? section.querySelector('.column_image img') : null;
        this._columnImageOriginalSrc = this._columnImageEl ? this._columnImageEl.src : null;
        this._columnImageDynamic = false; // true when we created the div ourselves
        this._columnDataEl = section ? section.querySelector('.column_data') : null;

        // Store reference to .column_data for chroma-mode class toggling
        this._columnDataEl = section ? section.querySelector('.column_data') : null;

        // Load variant and bundle from URL parameters on component mount
        this.loadVariantFromUrl();
        this.loadBundleFromUrl();

        // Auto-select attributes that have only one option
        this.preselectSingleOptions();

        // Apply initial pixel format visibility based on pre-selected chroma
        this.updatePixelFormatVisibility(this.getSelectedChroma());

        // Update datasheet link to match current URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        this.updateDatasheetLink(urlParams);

        // Smooth open/close animation for the accessories accordions
        this.$nextTick(() => initAccordions(this.$el));

        // Close the accessory info popover on outside click, scroll, or Escape —
        // it's teleported to <body> so it can escape the accordion's overflow:hidden.
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleDetailsKeydown);
        window.addEventListener('scroll', this.closeItemDetails, true);
        window.addEventListener('resize', this.closeItemDetails);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleDetailsKeydown);
        window.removeEventListener('scroll', this.closeItemDetails, true);
        window.removeEventListener('resize', this.closeItemDetails);
    },

    watch: {
        selectedVariant(newValue) {
            // Update URL when variant selection changes
            this.updateUrl();
            // Update pixel format visibility based on new variant's chroma
            this.updatePixelFormatVisibility(this.getSelectedChroma());
            // Fetch and apply variant-specific spec values
            this.fetchVariantSpecs(newValue);
        },
        selectedBundle(newValue) {
            // Update URL when bundle selection changes
            this.updateUrl();
        },
        matchedVariant(newVariant) {
            this.updateColumnImage(newVariant);
        },
        'variantSelections.chroma'(value) {
            this.updatePixelFormatVisibility(value);
        },
    },

    methods: {
        async addToBookmarks() {
            if (this.isLoading) return;

            if (this.hasVariants && !this.matchedVariant) {
                this.showToast(this.translations.chooseVariant || 'Please select a variant', 'error');
                return;
            }

            this.isLoading = true;

            try {
                // Parse bundle selection (format: "bundleID:productID")
                let bundleProductID = null;
                if (this.selectedBundle) {
                    const [bundleID, productID] = this.selectedBundle.split(':');
                    bundleProductID = parseInt(productID);
                }

                // Collect accessory selections
                const accessories = this.getAccessorySelections();

                const response = await fetch(this.apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        productID: this.productID,
                        variantID: this.selectedVariant || null,
                        bundleProductID: bundleProductID,
                        accessories: accessories,
                    }),
                });

                const data = await response.json();

                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Failed to add to bookmarks');
                }

                this.showToast(
                    data.message || this.translations.success,
                    'success'
                );

                // Update bookmark count badge in header
                if (window.updateBookmarkCount) {
                    window.updateBookmarkCount();
                }

            } catch (error) {
                console.error('Error adding to bookmarks:', error);
                this.showToast(
                    error.message || this.translations.error,
                    'error'
                );
            } finally {
                this.isLoading = false;
            }
        },

        showToast(message, type = 'success') {
            this.$toast.open({
                message: message,
                type: type,
                position: 'top-right',
                duration: 5000,
            });
        },

        /**
         * Auto-select attributes that have only one available option
         * and no __none__ virtual option (meaning all variants have this value).
         */
        preselectSingleOptions() {
            for (const group of this.variantAttributeGroups) {
                if (group.options.length === 1 && !this.variantSelections[group.key]) {
                    // Only preselect if the single option is a real value (not __none__)
                    if (group.options[0].value !== '__none__') {
                        this.variantSelections[group.key] = group.options[0].value;
                    }
                }
            }
        },

        /**
         * Set a variant attribute.
         * Only clears other groups whose selection directly conflicts
         * with the newly chosen value (ignoring other groups' selections).
         */
        setVariantAttribute(key, value) {
            this.variantSelections[key] = value;

            // Find which other groups conflict directly with the new key=value.
            // Check each other group in isolation against only the new selection,
            // not against all other groups' current selections.
            for (const otherKey of this.activeVariantKeys) {
                if (otherKey === key) continue;
                const currentSel = this.variantSelections[otherKey];
                if (!currentSel) continue;

                // Is there any variant where key=value AND otherKey=currentSel?
                const matchKey = (value === '__none__')
                    ? (v => { const a = v.attributes?.[key] || ''; return a === ''; })
                    : (v => (v.attributes?.[key] || '') === value);
                const matchOther = (currentSel === '__none__')
                    ? (v => { const a = v.attributes?.[otherKey] || ''; return a === ''; })
                    : (v => (v.attributes?.[otherKey] || '') === currentSel);

                const compatible = this.variants.some(v => matchKey(v) && matchOther(v));
                if (!compatible) {
                    this.variantSelections[otherKey] = '';
                }
            }

            // Auto-preselect single remaining options after clearing
            this.preselectSingleOptions();
        },

        /**
         * Check if a specific option value is still reachable given current
         * selections in other attribute groups.
         * __none__ matches variants with empty attribute values.
         */
        isOptionAvailable(key, value) {
            const otherKeys = this.activeVariantKeys.filter(k => k !== key);

            return this.variants.some(v => {
                const attr = v.attributes?.[key] || '';
                if (value === '__none__') {
                    if (attr !== '') return false;
                } else {
                    if (attr !== value) return false;
                }
                return otherKeys.every(ok => {
                    if (!this.variantSelections[ok]) return true;
                    const otherAttr = v.attributes?.[ok] || '';
                    if (this.variantSelections[ok] === '__none__') {
                        return otherAttr === '';
                    }
                    return otherAttr === this.variantSelections[ok];
                });
            });
        },

        /**
         * Load variant selection from URL parameter.
         * Restores individual attribute selections from the matched variant.
         */
        loadVariantFromUrl() {
            if (!this.hasVariants) return;

            const urlParams = new URLSearchParams(window.location.search);
            const variantParam = urlParams.get('variant');

            if (variantParam) {
                const variantId = parseInt(variantParam);
                this.urlVariantId = variantId;
                const variant = this.variants.find(v => v.id === variantId);

                if (variant && variant.attributes) {
                    for (const key of this.activeVariantKeys) {
                        const val = variant.attributes[key] || '';
                        if (val) {
                            this.variantSelections[key] = val;
                        } else {
                            // Variant has no value for this key — check if __none__ option exists
                            const group = this.variantAttributeGroups.find(g => g.key === key);
                            if (group && group.options.some(o => o.value === '__none__')) {
                                this.variantSelections[key] = '__none__';
                            }
                        }
                    }
                }
            }
        },

        /**
         * Load bundle selection from URL parameter
         */
        loadBundleFromUrl() {
            if (!this.hasBundles) return;

            const urlParams = new URLSearchParams(window.location.search);
            const bundleParam = urlParams.get('bundle');

            if (bundleParam) {
                // Validate bundle format (bundleID:productID)
                const bundlePattern = /^\d+:\d+$/;
                if (bundlePattern.test(bundleParam)) {
                    this.selectedBundle = bundleParam;
                }
            }
        },

        /**
         * Update URL with selected variant and bundle (without page reload)
         */
        updateUrl() {
            const urlParams = new URLSearchParams(window.location.search);

            if (this.selectedVariant) {
                urlParams.set('variant', this.selectedVariant);
            } else {
                urlParams.delete('variant');
            }

            if (this.selectedBundle) {
                urlParams.set('bundle', this.selectedBundle);
            } else {
                urlParams.delete('bundle');
            }

            const newUrl = urlParams.toString()
                ? `${window.location.pathname}?${urlParams.toString()}`
                : window.location.pathname;

            // Update URL without page reload
            window.history.pushState({}, '', newUrl);

            // Update datasheet link with same parameters
            this.updateDatasheetLink(urlParams);
        },

        /**
         * Update the datasheet link href with current variant/bundle parameters
         */
        updateDatasheetLink(urlParams) {
            const datasheetLink = document.getElementById('datasheet-link');
            if (!datasheetLink) return;

            // Get the base href (without query parameters)
            const baseHref = datasheetLink.href.split('?')[0];

            // Append query parameters if present
            const newHref = urlParams.toString()
                ? `${baseHref}?${urlParams.toString()}`
                : baseHref;

            datasheetLink.href = newHref;
        },

        /**
         * Toggle spec details visibility for an accessory item.
         * Only one popover is open at a time; the button's screen position is
         * captured on open so the teleported popover can be placed via fixed
         * positioning (native popovertarget is unreliable on iOS inside a <form>,
         * and CSS anchor positioning isn't supported on older Safari).
         */
        toggleItemDetails(categoryKey, itemId, event) {
            const key = `${categoryKey}-${itemId}`;
            const isOpening = !this.openItemDetails[key];

            this.openItemDetails = {};

            if (isOpening) {
                const rect = event.currentTarget.getBoundingClientRect();
                this.itemDetailsPosition = {
                    [key]: {
                        top: rect.bottom + 5,
                        right: window.innerWidth - rect.right - 15,
                    },
                };
                this.openItemDetails = { [key]: true };
            }
        },

        /**
         * Check if spec details are open for an accessory item
         */
        isItemDetailsOpen(categoryKey, itemId) {
            return !!this.openItemDetails[`${categoryKey}-${itemId}`];
        },

        /**
         * Fixed-position inline style for the teleported accessory info popover
         */
        getItemDetailsStyle(categoryKey, itemId) {
            const pos = this.itemDetailsPosition[`${categoryKey}-${itemId}`];
            if (!pos) return {};
            return {
                position: 'fixed',
                top: `${pos.top}px`,
                right: `${pos.right}px`,
            };
        },

        closeItemDetails() {
            // Nur schreiben, wenn wirklich etwas offen ist. Der Handler haengt an
            // document und lief bisher bei JEDEM Klick — auch auf eine Checkbox.
            // Die Zuweisung eines neuen leeren Objekts loeste dann ein Re-Render
            // mitten im Klick aus, und die v-model-Direktive setzte den gerade
            // umgeschalteten DOM-Zustand aus dem noch leeren Modell zurueck.
            if (Object.keys(this.openItemDetails).length === 0) {
                return;
            }

            this.openItemDetails = {};
        },

        /**
         * Zubehoerauswahl umschalten.
         *
         * Bewusst ohne v-model: dessen change-Handler leitet den neuen Zustand aus
         * el.checked ab. Faellt zwischen click und change ein Re-Render an, hat die
         * Direktive el.checked bereits aus dem Modell zurueckgesetzt, und die
         * Auswahl geht verloren. Aus dem Modell abgeleitet kann das nicht passieren.
         */
        /**
         * Name shown in the selection list: the short name of a cable, otherwise the title.
         */
        accessoryShownName(item) {
            return item.shortTitle || item.title;
        },

        /**
         * Text for screen readers: words of a product name are separated by hyphens.
         */
        readableName(text) {
            return String(text ?? '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
        },

        moreInformationLabel(item) {
            const template = this.accessoriesTranslations.moreInformationAbout || 'More information about {name}';
            return template.replace('{name}', this.readableName(this.accessoryShownName(item)));
        },

        toggleAccessory(categoryKey, itemID) {
            const selected = this.selectedAccessories[categoryKey];

            if (!Array.isArray(selected)) {
                return;
            }

            const index = selected.indexOf(itemID);

            if (index === -1) {
                selected.push(itemID);
            } else {
                selected.splice(index, 1);
            }
        },

        handleOutsideClick(event) {
            if (
                event.target.closest('.selection_container__accessory-item-details') ||
                event.target.closest('.selection_container__accessory-item-info-toggle')
            ) {
                return;
            }
            this.closeItemDetails();
        },

        handleDetailsKeydown(event) {
            if (event.key === 'Escape') {
                this.closeItemDetails();
            }
        },

        /**
         * Build a product image proxy URL for the given path, including fit dimensions.
         */
        buildImageUrl(path) {
            const { width, height } = this.fitParams;
            let url = `/product-image/?path=${encodeURIComponent(path)}`;
            if (width) url += `&width=${width}`;
            if (height) url += `&height=${height}`;
            if (this.imageOptimizeFormat) url += '&optimizeFormat=1';
            return url;
        },

        /**
         * Swap the .column_image img src when the matched variant changes.
         *
         * Handles three cases:
         * 1. column_image already exists in the DOM — update src or restore original
         * 2. column_image does not exist (no static image on this product) —
         *    create it dynamically and remove the section--no-image layout class
         * 3. Variant loses its image — remove the dynamically created column_image
         *    and restore the section--no-image class
         */
        updateColumnImage(variant) {
            const imagePath = variant?.productImageImport;

            if (!imagePath) {
                if (this._columnImageEl && this._columnImageOriginalSrc) {
                    // Static image existed before — restore it
                    this._columnImageEl.src = this._columnImageOriginalSrc;
                } else if (this._columnImageEl && this._columnImageDynamic) {
                    // We created it — remove it and restore no-image layout
                    this._columnImageEl.closest('.column_image')?.remove();
                    this._columnImageEl = null;
                    this._columnImageDynamic = false;
                    this._sectionParentEl?.classList.add('section--no-image');
                }
                return;
            }

            const newSrc = this.buildImageUrl(imagePath);

            if (this._columnImageEl) {
                // Element exists — just update src
                if (this._columnImageEl.src !== newSrc) {
                    this._columnImageEl.src = newSrc;
                }
                return;
            }

            // No column_image in DOM — create it dynamically
            if (!this._sectionEl) return;

            const div = document.createElement('div');
            div.className = 'column_image';
            const img = document.createElement('img');
            img.src = newSrc;
            img.alt = this.productTitle;
            div.appendChild(img);

            this._sectionEl.insertBefore(div, this._sectionEl.firstChild);
            this._columnImageEl = img;
            this._columnImageDynamic = true;
            this._sectionParentEl?.classList.remove('section--no-image');
        },

        /**
         * Fetch spec groups for the given variant ID and update the spec table in the DOM.
         * Uses AbortController to cancel any in-flight request when the variant changes
         * before the previous response arrives (prevents stale data overwriting newer results).
         */
        async fetchVariantSpecs(variantId) {
            if (!this.specsUrl) return;

            // Abort any previous in-flight request
            if (this._specsFetchController) {
                this._specsFetchController.abort();
            }
            this._specsFetchController = new AbortController();
            const { signal } = this._specsFetchController;

            const url = variantId
                ? `${this.specsUrl}?variantID=${variantId}`
                : this.specsUrl;

            try {
                const response = await fetch(url, { signal });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json();
                this.applySpecGroups(data.groups);
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error fetching variant specs:', error);
            }
        },

        /**
         * Update spec values in-place without touching the DOM structure.
         * Preserves <details> open/closed state and only swaps changed text.
         *
         * For variant-only rows (Housing, OpticalFilter, CameraAngle) that don't exist
         * in the server-rendered HTML, rows are inserted at the correct position and
         * marked with data-variant-only so they can be removed on variant deselect.
         */
        applySpecGroups(groups) {
            const sectionTable = this.$el.closest('.section_content')
                ?.querySelector('.section_table');
            if (!sectionTable) return;

            // Remove previously injected variant-only rows
            sectionTable.querySelectorAll('[data-variant-only]').forEach(el => el.remove());

            groups.forEach(group => {
                const details = sectionTable.querySelector(
                    `[data-group-slug="${group.groupSlug}"]`
                );
                if (!details) return;
                const table = details.querySelector('.spec-table');
                if (!table) return;

                let tbody = table.querySelector('tbody');
                if (!tbody) {
                    tbody = document.createElement('tbody');
                    table.appendChild(tbody);
                }

                group.fields.forEach(field => {
                    if (field.variantOnly) {
                        // Build and insert the row — it has no counterpart in the base HTML
                        const tr = this._createSpecRow(field);
                        tr.dataset.variantOnly = 'true';

                        if (field.insertAfter) {
                            const anchor = tbody.querySelector(
                                `[data-field-key="${field.insertAfter}"]`
                            );
                            if (anchor) {
                                anchor.after(tr);
                            } else {
                                tbody.appendChild(tr);
                            }
                        } else {
                            // No anchor → prepend (used for Housing)
                            tbody.prepend(tr);
                        }
                    } else {
                        // Update the value cell of the existing row in-place
                        const existingRow = tbody.querySelector(
                            `[data-field-key="${field.fieldKey}"]`
                        );
                        if (existingRow) {
                            existingRow.querySelector('.table_value').textContent = field.value;
                        }
                    }
                });
            });

            this.updatePixelFormatVisibility(this.getSelectedChroma());
        },

        /**
         * Create a spec table row element from a field object.
         */
        _createSpecRow(field) {
            const tr = document.createElement('tr');
            tr.dataset.fieldKey = field.fieldKey;
            if (field.rowClass) tr.className = field.rowClass;

            const tdTitle = document.createElement('td');
            tdTitle.className = 'table_title';
            tdTitle.textContent = field.title + ':';

            const tdValue = document.createElement('td');
            tdValue.className = 'table_value';
            tdValue.textContent = field.value;

            tr.appendChild(tdTitle);
            tr.appendChild(tdValue);
            return tr;
        },

        /**
         * Return the chroma mode ('mono', 'color' or '') of the currently selected
         * variant. The mode is resolved server-side, so labels like "Color/Mono" or
         * "Mono polarized" are interpreted with the same rules as everywhere else.
         */
        getSelectedChroma() {
            if (!this.selectedVariant) return '';
            const variant = this.variants.find(v => v.id === this.selectedVariant);
            return variant?.chromaMode || '';
        },

        /**
         * Toggle pixel format row visibility based on chroma selection.
         * Adds chroma-mode--mono or chroma-mode--color to .column_data;
         * CSS rules handle hiding the irrelevant rows. An empty mode leaves both
         * classes off, so a variant covering both modes shows all rows.
         */
        updatePixelFormatVisibility(chroma) {
            if (!this._columnDataEl) return;
            const value = (chroma || '').toLowerCase();
            this._columnDataEl.classList.toggle('chroma-mode--mono', value === 'mono');
            this._columnDataEl.classList.toggle('chroma-mode--color', value === 'color');
        },

        /**
         * Build accessory selections array from internal state
         */
        getAccessorySelections() {
            const selections = [];

            for (const category of this.accessoryCategories) {
                const ids = this.selectedAccessories[category.key] || [];
                for (const id of ids) {
                    selections.push({
                        accessoryType: category.type,
                        accessoryId: id,
                    });
                }
            }

            return selections;
        },
    },
};
</script>

<style scoped>
button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.bundle-description {
    display: block;
    font-size: 0.875em;
    color: #666;
    margin-top: 0.25rem;
    font-weight: normal;
}

.bundle-info-link {
    display: inline-block;
    margin-left: 0.5rem;
    font-size: 0.875em;
    text-decoration: underline;
    color: #0066cc;
    cursor: pointer;
    font-weight: normal;
}

.bundle-info-link:hover {
    color: #004499;
}

.selected-product {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.selected-product-info {
    flex: 1;
    order: 1;
}

.selected-product-image {
    order: 0;
    width: 80px;
    height: auto;
    object-fit: contain;
    flex-shrink: 0;
}

.selected-product-label {
    margin: 0 0 0.25rem;
    font-size: 0.75em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.6;
}

.selected-product-label--accessories {
    margin-top: 0.75rem;
}
</style>

<style>
/* Global toast customization */
</style>
